import { useState, useCallback } from 'react';
import { Search, Loader2, LayoutDashboard, FolderKanban, CheckSquare, Database, Wallet, Shield, Megaphone, Calendar, MessageCircle, Mail, Bot, Contact, Users, Activity, Plug } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SearchResultItem {
  type: string;
  title: string;
  subtitle?: string;
  link?: string;
}

interface SearchResultCategory {
  category: string;
  icon: any;
  items: SearchResultItem[];
}

const categoryConfig: Record<string, { label: string; icon: any; group: string; link: string }> = {
  projects: { label: 'Projekte', icon: FolderKanban, group: 'Navigation', link: '/dashboard/projekte' },
  tasks: { label: 'Aufgaben', icon: CheckSquare, group: 'Navigation', link: '/dashboard/aufgaben' },
  bookings: { label: 'Finanzen', icon: Wallet, group: 'Navigation', link: '/dashboard/finanzen' },
  marketing: { label: 'Marketing', icon: Megaphone, group: 'Navigation', link: '/dashboard/marketing' },
  contacts: { label: 'Kontakte', icon: Contact, group: 'Kommunikation', link: '/dashboard/kontakte' },
};

const parseAIResponse = (response: string): SearchResultCategory[] => {
  const categories: SearchResultCategory[] = [];

  // Try to extract structured sections from AI response
  const sections = response.split(/\*\*([^*]+)\*\*/g);
  
  let currentLabel = '';
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i].trim();
    if (!section) continue;
    
    // Check if this is a section header
    if (i % 2 === 1) {
      currentLabel = section.replace(/[:\(\)]/g, '').trim();
      continue;
    }

    // Extract list items
    const lines = section.split('\n').filter(l => l.trim().startsWith('-'));
    if (lines.length === 0) continue;

    // Find matching config
    const configKey = Object.keys(categoryConfig).find(key => 
      currentLabel.toLowerCase().includes(categoryConfig[key].label.toLowerCase())
    );

    const config = configKey ? categoryConfig[configKey] : null;
    
    const items: SearchResultItem[] = lines.map(line => {
      const cleanLine = line.replace(/^-\s*/, '').trim();
      const parts = cleanLine.split(/\s*\(/, 2);
      return {
        type: configKey || 'general',
        title: parts[0].trim(),
        subtitle: parts[1] ? parts[1].replace(/\)$/, '').trim() : undefined,
        link: config?.link,
      };
    });

    if (items.length > 0) {
      categories.push({
        category: config?.label || currentLabel || 'Ergebnisse',
        icon: config?.icon || Search,
        items,
      });
    }
  }

  // If parsing failed, create a single "Ergebnisse" category
  if (categories.length === 0 && response.trim()) {
    const lines = response.split('\n').filter(l => l.trim());
    categories.push({
      category: 'Ergebnisse',
      icon: Search,
      items: lines.slice(0, 20).map(line => ({
        type: 'general',
        title: line.replace(/^[-*•]\s*/, '').trim(),
      })),
    });
  }

  return categories;
};

export const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResultCategory[]>([]);
  const [rawResponse, setRawResponse] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    setIsSearching(true);
    setResults([]);
    setRawResponse(null);

    try {
      const response = await supabase.functions.invoke('ai-assistant', {
        body: {
          messages: [],
          newMessage: `Suche nach "${query}". Bitte strukturiere deine Antwort klar nach Kategorien (Projekte, Aufgaben, Kontakte, Finanzen, Marketing) und liste die relevanten Ergebnisse als Aufzählung auf. Zeige nur Kategorien an, in denen etwas gefunden wurde.`,
        },
      });

      if (response.error) throw response.error;

      const aiResponse = response.data?.response || 'Keine Ergebnisse gefunden.';
      setRawResponse(aiResponse);
      setResults(parseAIResponse(aiResponse));
    } catch (error: any) {
      toast.error('Suchfehler: ' + error.message);
    } finally {
      setIsSearching(false);
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  const handleNavigate = (link?: string) => {
    if (link) {
      navigate(link);
      handleClose();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setQuery('');
      setResults([]);
      setRawResponse(null);
    }, 200);
  };

  const groupedResults = results.reduce<Record<string, SearchResultCategory[]>>((acc, cat) => {
    const configEntry = Object.values(categoryConfig).find(c => c.label === cat.category);
    const group = configEntry?.group || 'Sonstiges';
    if (!acc[group]) acc[group] = [];
    acc[group].push(cat);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="p-0 flex flex-col border border-border bg-card overflow-hidden"
        style={{ width: 640, maxWidth: '95vw', maxHeight: '85vh' }}
      >
        <div className="shrink-0 px-6 py-4 border-b bg-secondary/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              Suche in NexaBoard
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-hidden p-6 flex flex-col gap-4">
          <div className="flex gap-2">
            <Input
              placeholder="Wonach suchen Sie?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
              autoFocus
            />
            <Button 
              variant="brand"
              onClick={handleSearch} 
              disabled={isSearching || !query.trim()}
              className="gap-2"
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Suchen
            </Button>
          </div>

          {isSearching && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-3" />
              <span className="text-sm">NexaBoard wird durchsucht...</span>
            </div>
          )}

          {results.length > 0 && !isSearching && (
            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-5">
                {Object.entries(groupedResults).map(([group, cats]) => (
                  <div key={group}>
                    <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      {group}
                    </h3>
                    <div className="space-y-2">
                      {cats.map((cat, catIdx) => (
                        <div key={catIdx} className="rounded-lg border border-border bg-secondary/20 overflow-hidden">
                          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/50 bg-secondary/30">
                            <cat.icon className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium text-foreground">{cat.category}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto">
                              {cat.items.length} {cat.items.length === 1 ? 'Ergebnis' : 'Ergebnisse'}
                            </span>
                          </div>
                          <div className="divide-y divide-border/30">
                            {cat.items.map((item, itemIdx) => (
                              <div
                                key={itemIdx}
                                className={`flex items-center gap-3 px-4 py-2 text-sm ${item.link ? 'cursor-pointer hover:bg-secondary/50 transition-colors' : ''}`}
                                onClick={() => handleNavigate(item.link)}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-foreground truncate">{item.title}</p>
                                  {item.subtitle && (
                                    <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                                  )}
                                </div>
                                {item.link && (
                                  <span className="text-[10px] text-primary shrink-0">Öffnen →</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {results.length === 0 && rawResponse && !isSearching && (
            <ScrollArea className="flex-1 min-h-0">
              <div className="rounded-lg border border-border bg-secondary/20 p-4">
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{rawResponse}</p>
              </div>
            </ScrollArea>
          )}

          {!rawResponse && !isSearching && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="h-12 w-12 rounded-full bg-secondary/50 flex items-center justify-center">
                <Search className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground text-center max-w-xs">
                Durchsuchen Sie Projekte, Aufgaben, Kontakte, Finanzen und mehr innerhalb von NexaBoard.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
